import React, { useMemo } from 'react'
import styles from './Nav.module.scss'
import { NavContext } from './NavContext'
import { NavItem, TNavItemVariant } from './NavItem'
import { NavSubItem } from './NavSubItem'

export interface INavProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  /**
   * Whether the Nav component is collapsed.
   */
  isCollapsed?: boolean
  /**
   * Can be 'filled' or 'transparent'.
   */
  variant?: TNavItemVariant
}

/**
 * The Nav component is a container for `Nav.Item` components.
 * It can be used to create a navigation menu.
 * Props passed to the Nav component will be applied to all `Nav.Item` components.
 */
export const Nav = ({ children, variant = 'filled', isCollapsed = false, ...props }: INavProps) => {
  const contextValue = useMemo(() => ({ variant, isCollapsed }), [variant, isCollapsed])

  return (
    <NavContext.Provider value={contextValue}>
      <nav className={styles.nav} {...props}>
        {children}
      </nav>
    </NavContext.Provider>
  )
}

/**
 * The `Nav.Item` component is a container for the content of a navigation item.
 * @important The `Nav.Item` component should be used inside the Nav component
 */
Nav.Item = NavItem

/**
 * The `Nav.SubItem` component is a container for the content of a sub-item.
 * @important The `Nav.SubItem` components should be used as `subItems` prop for `Nav.Item` component
 */
Nav.SubItem = NavSubItem
